import { BUSINESS, SITE_URL, type ServiceInfo, type CityInfo, serviceHref } from "./site";
import { IMAGES } from "./images";

/** HousekeepingService (LocalBusiness) schema — placed on every page. */
export function localBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "HousekeepingService",
    name: BUSINESS.name,
    description: `${BUSINESS.offers} in ${BUSINESS.city}, ${BUSINESS.state} and surrounding areas. ${BUSINESS.tagline}`,
    url: SITE_URL,
    telephone: BUSINESS.phoneHref.replace("tel:", ""),
    email: BUSINESS.email,
    logo: IMAGES.logo,
    image: IMAGES.coverBanner,
    areaServed: BUSINESS.areaServed.map((c) => ({
      "@type": "City",
      name: c,
    })),
    sameAs: [BUSINESS.facebook],
    address: {
      "@type": "PostalAddress",
      addressLocality: BUSINESS.city,
      addressRegion: BUSINESS.state,
      addressCountry: "US",
    },
    knowsAbout: [
      "Residential cleaning",
      "Commercial cleaning",
      "Deep cleaning",
      "Move-in and move-out cleaning",
      "Post-construction cleaning",
      "Recurring house cleaning",
    ],
  };
}

/** Service schema for an individual service page. */
export function serviceSchema(service: ServiceInfo) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: service.name,
    name: `${service.name} in ${BUSINESS.city}, ${BUSINESS.state}`,
    description: service.blurb,
    url: `${SITE_URL}${serviceHref(service.slug)}`,
    provider: {
      "@type": "HousekeepingService",
      name: BUSINESS.name,
      telephone: BUSINESS.phoneHref.replace("tel:", ""),
      areaServed: BUSINESS.areaServed.map((c) => ({ "@type": "City", name: c })),
    },
    areaServed: BUSINESS.areaServed.map((c) => ({ "@type": "City", name: c })),
  };
}

/** FAQPage schema from a list of {question, answer}. */
export function faqSchema(items: { question: string; answer: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((q) => ({
      "@type": "Question",
      name: q.question,
      acceptedAnswer: { "@type": "Answer", text: q.answer },
    })),
  };
}

/** BreadcrumbList schema from visible crumbs [{name, path}]. */
export function breadcrumbSchema(crumbs: { name: string; path: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: `${SITE_URL}${c.path === "/" ? "" : c.path}`,
    })),
  };
}

/** LocalBusiness-with-areaServed for a city page. */
export function cityBusinessSchema(city: CityInfo) {
  return {
    "@context": "https://schema.org",
    "@type": "HousekeepingService",
    name: BUSINESS.name,
    description: `${BUSINESS.offers} serving ${city.name}, ${city.state} and nearby communities.`,
    url: `${SITE_URL}/service-areas/${city.slug}`,
    telephone: BUSINESS.phoneHref.replace("tel:", ""),
    email: BUSINESS.email,
    areaServed: { "@type": "City", name: `${city.name}, ${city.state}` },
    sameAs: [BUSINESS.facebook],
  };
}
